import {create} from 'zustand'
import {IResLogin, IUserPreferences, TokenAuth} from '../types/auth'
import {STORAGE_NAMES} from '../helpers/consts'
import {useUserPreferencesStore} from './userPreferencesStore'

type State = {
  user: TokenAuth | null
  status: 'no-user' | 'user' | 'loading'
}

type Actions = {
  login: (res: IResLogin | TokenAuth | null | undefined) => void
  logout: () => void
  setLoading: () => void
  syncUserProfile: (userPatch: Partial<TokenAuth['user']>) => void
}

const getTokenAuth = (res: IResLogin | TokenAuth | null | undefined): TokenAuth | null => {
  if (!res) return null
  if ('tokenAuth' in res) {
    return res.tokenAuth || null
  }
  return res
}

export const useGetUser = create<State & Actions>((set) => ({
  user: null,
  status: 'loading',
  login: (res) => {
    const tokenAuth = getTokenAuth(res)

    if (tokenAuth) {
      set(() => ({user: tokenAuth, status: 'user'}))
      window.localStorage.setItem(STORAGE_NAMES.TOKEN, tokenAuth.token)
      const {settings} = tokenAuth
      if (settings && settings.length > 0) {
        try {
          const jsSettings = JSON.parse(settings[0]) as {id: number; organization_id: number}
          if (jsSettings?.id) {
            window.localStorage.setItem(STORAGE_NAMES.ID_USER, `${jsSettings.id}`)
          }
        } catch (error) {
          console.log('error', error)
        }
      }
      window.localStorage.setItem(STORAGE_NAMES.USER, JSON.stringify(tokenAuth))

      if (tokenAuth.user?.preferences) {
        try {
          // Backend returns graphene.JSONString — may arrive as a string or already-parsed object
          const rawPrefs = tokenAuth.user.preferences as unknown
          const prefs =
            typeof rawPrefs === 'string'
              ? (JSON.parse(rawPrefs) as IUserPreferences)
              : (rawPrefs as IUserPreferences)
          // Only override local state if backend has actual saved preferences
          const hasData =
            prefs?.sidebar_view != null ||
            prefs?.language != null ||
            (Array.isArray(prefs?.sidebar_favorites) && prefs.sidebar_favorites.length > 0)
          if (hasData) {
            useUserPreferencesStore.getState().hydrate(prefs)
          }
        } catch {
          // ignore malformed JSON
        }
      }
    }
  },
  logout: () => {
    set(() => ({user: null, status: 'no-user'}))
    window.localStorage.removeItem(STORAGE_NAMES.TOKEN)
    window.localStorage.removeItem(STORAGE_NAMES.USER)
    window.localStorage.removeItem(STORAGE_NAMES.ID_USER)
    useUserPreferencesStore.getState().reset()
  },
  setLoading: () => set(() => ({status: 'loading'})),
  syncUserProfile: (userPatch) =>
    set((state) => {
      if (!state.user) return state

      const updatedUser = {
        ...state.user,
        user: {
          ...state.user.user,
          ...userPatch,
        },
      }

      window.localStorage.setItem(STORAGE_NAMES.USER, JSON.stringify(updatedUser))
      return {user: updatedUser}
    }),
}))
